import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, Pencil } from "lucide-react";

export default function CartItem({ 
  item, 
  onUpdateQuantity, 
  onRemove,
  onSelect,
  index = 0, 
  displaySettings = {},
  hasMixMatch = false,
  hasBuydown = false,
  isSelected = false,
  discount = 0
}) {
  // Alternate row colors: odd index = light gray, even index = white
  const bgColor = index % 2 === 0 ? "bg-zinc-100" : "bg-white";
  
  // Check if item is taxable (tax_1 or tax_2)
  const isTaxable = item.tax_1 || item.tax_2 || item.tax1 || item.tax2;
  // Check if item is foodstampable (EBT eligible)
  const isFoodstampable = item.foodstamp || item.foodstampable || item.ebt_eligible;
  // Check if price was edited
  const isPriceEdited = item.priceEdited === true;
  
  // Cart display settings with defaults
  const cartDisplay = displaySettings.cart_display || {};
  const itemNameFont = cartDisplay.item_name_font || "Inter";
  const itemNameSize = cartDisplay.item_name_size || "17";
  const itemNameWeight = cartDisplay.item_name_weight || "500";
  const itemUpcFont = cartDisplay.item_upc_font || "Inter";
  const itemUpcSize = cartDisplay.item_upc_size || "12";
  const itemUpcWeight = cartDisplay.item_upc_weight || "400";
  const eachPriceFont = cartDisplay.each_price_font || "monospace";
  const eachPriceSize = cartDisplay.each_price_size || "14";
  const eachPriceWeight = cartDisplay.each_price_weight || "400";
  const priceFont = cartDisplay.price_font || "monospace";
  const priceSize = cartDisplay.price_size || "18";
  const priceWeight = cartDisplay.price_weight || "700";
  
  // Selection border style
  const selectionStyle = isSelected 
    ? "ring-2 ring-blue-500 border-blue-500" 
    : "border-zinc-200";
  
  return (
    <div
      className={`relative flex py-1.5 px-3 rounded-lg border select-none cursor-pointer ${bgColor} ${selectionStyle}`}
      data-testid={`cart-item-${item.product_id}`}
      onClick={() => onSelect && onSelect(item.product_id)}
    >
      {/* Left side: Indicator bars */}
      <div className="flex flex-col gap-0.5 mr-2 flex-shrink-0 justify-center w-2">
        {isFoodstampable && (
          <span 
            className="w-1.5 h-6 rounded-sm bg-green-500" 
            title="Foodstampable (EBT Eligible)"
            data-testid={`foodstamp-indicator-${item.product_id}`}
          />
        )}
        {isTaxable && (
          <span 
            className="w-1.5 h-6 rounded-sm bg-pink-500" 
            title="Taxable"
            data-testid={`tax-indicator-${item.product_id}`}
          />
        )}
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col justify-center min-w-0">
        {/* Top row: Name only */}
        <div className="flex items-center whitespace-nowrap overflow-hidden">
          <span 
            className="text-zinc-900 truncate leading-tight"
            style={{ 
              fontFamily: itemNameFont, 
              fontSize: `${itemNameSize}px`,
              fontWeight: itemNameWeight
            }}
          >
            {item.product_name}
          </span>
        </div>
        
        {/* Bottom row: Price each + SKU */}
        <div className="flex items-center">
          <p 
            className="text-zinc-600 flex-shrink-0"
            style={{ 
              fontFamily: eachPriceFont, 
              fontSize: `${eachPriceSize}px`,
              fontWeight: eachPriceWeight
            }}
          >
            ${item.price.toFixed(2)} each
          </p>
          {item.sku && (
            <>
              <span className="text-zinc-400 flex-shrink-0 mx-2">|</span>
              <span 
                className="text-zinc-500 flex-shrink-0"
                style={{ 
                  fontFamily: itemUpcFont, 
                  fontSize: `${itemUpcSize}px`,
                  fontWeight: itemUpcWeight
                }}
              >
                {item.sku}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Floating absolute: Qty controls - vertically centered */}
      <div 
        className="absolute flex items-center gap-1"
        style={{ top: '50%', transform: 'translateY(-50%)', right: '140px' }}
        data-testid={`qty-controls-${item.product_id}`}
      >
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0 rounded-md border-zinc-300 hover:bg-zinc-200"
          onClick={(e) => { e.stopPropagation(); onUpdateQuantity(item.product_id, item.quantity - 1); }}
          data-testid={`qty-minus-${item.product_id}`}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <span 
          className="min-w-[24px] text-center"
          style={{ 
            fontFamily: priceFont, 
            fontSize: `${priceSize}px`,
            fontWeight: priceWeight
          }}
        >
          {item.quantity}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0 rounded-md border-zinc-300 hover:bg-zinc-200"
          onClick={(e) => { e.stopPropagation(); onUpdateQuantity(item.product_id, item.quantity + 1); }}
          data-testid={`qty-plus-${item.product_id}`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Floating absolute: Amount - vertically centered */}
      <div 
        className="absolute flex items-center gap-1"
        style={{ top: '50%', transform: 'translateY(-50%)', right: '42px' }}
        data-testid={`amount-display-${item.product_id}`}
      >
        {isPriceEdited && (
          <Pencil className="w-3 h-3 text-orange-500" title="Price edited" />
        )}
        {discount > 0 ? (
          <div className="flex flex-col items-end">
            <span 
              className="text-zinc-400 line-through"
              style={{ 
                fontFamily: priceFont, 
                fontSize: `${Math.max(10, parseInt(priceSize) - 4)}px`,
                fontWeight: '400',
                lineHeight: '1'
              }}
            >
              ${item.subtotal.toFixed(2)}
            </span>
            <span 
              className="text-green-700"
              style={{ 
                fontFamily: priceFont, 
                fontSize: `${priceSize}px`,
                fontWeight: priceWeight,
                lineHeight: '1.1'
              }}
            >
              ${(item.subtotal - discount).toFixed(2)}
            </span>
          </div>
        ) : (
          <span 
            className={`text-right min-w-[70px] ${isPriceEdited ? 'text-orange-600' : ''}`}
            style={{ 
              fontFamily: priceFont, 
              fontSize: `${priceSize}px`,
              fontWeight: priceWeight
            }}
          >
            ${item.subtotal.toFixed(2)}
          </span>
        )}
      </div>

      {/* Floating absolute: Trash icon - vertically centered */}
      <Button
        size="sm"
        variant="ghost"
        className="absolute h-6 w-6 p-0 text-red-600 hover:bg-red-100"
        style={{ top: '50%', transform: 'translateY(-50%)', right: '12px' }}
        onClick={(e) => { e.stopPropagation(); onRemove(item.product_id); }}
        data-testid={`remove-item-${item.product_id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>

      {/* Right side: Promotion indicators (Mix & Match and Buydown) */}
      <div 
        className="absolute flex flex-col gap-0.5 justify-center"
        style={{ top: '50%', transform: 'translateY(-50%)', right: '2px' }}
      >
        {hasMixMatch && (
          <span 
            className="w-1.5 h-6 rounded-sm bg-yellow-400" 
            title="Mix & Match Deal"
            data-testid={`mixmatch-indicator-${item.product_id}`}
          />
        )}
        {hasBuydown && (
          <span 
            className="w-1.5 h-6 rounded-sm bg-blue-500" 
            title="MFG Buydown"
            data-testid={`buydown-indicator-${item.product_id}`}
          />
        )}
      </div>
    </div>
  );
}
